import React, { useMemo } from "react";
import { ContextRecipeCard } from "../recipe/ContextRecipeCard";
import { AnimatedSkeleton } from "../ui/animated-skeleton";
import {
  DynamicSkeleton,
  ProgressiveSkeleton,
  PROGRESSIVE_SKELETONS,
} from "../ui/progressive-skeleton";
import { Button } from "../ui/button";
import { RefreshCw, ChefHat } from "lucide-react";

interface RecipeGridProps {
  recipes: any[];
  loading?: boolean;
  likedRecipes?: string[];
  onLike?: (recipeId: string) => void;
  onAddToPlan?: (recipeId: string, dayIndex: number, mealType: string) => void;
  onViewDetails?: (recipeId: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onCreatePlan?: () => void;
  creating?: boolean;
}

export function RecipeGrid({
  recipes,
  loading = false,
  likedRecipes = [],
  onLike,
  onAddToPlan,
  onViewDetails,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onCreatePlan,
  creating = false,
}: RecipeGridProps) {
  const gridSkeletons = useMemo(() => PROGRESSIVE_SKELETONS.recipeGrid(8), []);
  // Skeleton Loading State
  if (loading) {
    return (
      <div className="mx-auto max-w-7xl p-4">
        <ProgressiveSkeleton
          skeletons={gridSkeletons}
          duration={1500}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        />
      </div>
    );
  }

  // Empty State
  if (!recipes || recipes.length === 0) {
    return (
      <div className="mx-auto max-w-7xl p-4">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-stone/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <ChefHat className="text-supporting h-8 w-8" />
          </div>
          <h3 className="mb-2 font-header text-lg font-medium text-dusk">
            No recipes found
          </h3>
          <p className="text-supporting max-w-md">
            Try adjusting your search terms or filters to discover more recipes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-4">
      {/* Recipe Grid - Reduced gap for better density */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {recipes.map((recipe: any, index: number) => (
          <ContextRecipeCard
            key={recipe.id}
            context="discover"
            recipe={{
              id: recipe.id,
              title: recipe.title || recipe.name,
              imageUrl: recipe.image || recipe.imageUrl || recipe.heroImageUrl,
              description: recipe.description,
              prepTime: recipe.prepTime || recipe.readyInMinutes,
              difficulty: recipe.difficulty,
              cuisine: recipe.cuisine,
              tags: recipe.tags || [],
              calories: recipe.calories || recipe.nutrition?.calories,
              servings: recipe.servings || recipe.yield,
              rating:
                recipe.rating || (recipe.spoonacularScore
                  ? recipe.spoonacularScore / 20
                  : undefined),
              slug: recipe.slug || recipe.id,
              createdAt: new Date(),
              updatedAt: new Date(),
            }}
            imageFetchPriority={index < 4 ? 'high' : 'auto'}
            isLiked={likedRecipes.includes(recipe.id)}
            onLike={onLike}
            onAddToPlan={onAddToPlan}
            onView={onViewDetails}
          />
        ))}
      </div>

      {/* Load More Button - Reduced spacing */}
      {hasMore && onLoadMore && (
        <div className="mt-8 flex justify-center">
          <Button
            onClick={onLoadMore}
            disabled={loadingMore}
            variant="outline"
            size="lg"
            className="min-w-32"
          >
            {loadingMore ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              "Load More Recipes"
            )}
          </Button>
        </div>
      )}

      {/* Floating Create Plan Button */}
      {likedRecipes.length > 0 && onCreatePlan && (
        <div className="fixed bottom-6 right-6 z-30">
          <Button
            onClick={onCreatePlan}
            disabled={creating || likedRecipes.length < 6}
            size="lg"
            className="min-w-36 bg-coral font-medium text-white shadow-lg transition-all duration-200 hover:bg-coral/90 hover:shadow-xl"
          >
            {creating ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <ChefHat className="mr-2 h-4 w-4" />
                Create Plan ({likedRecipes.length})
              </>
            )}
          </Button>

          {/* Helper tooltip for minimum selection */}
          {likedRecipes.length > 0 && likedRecipes.length < 6 && (
            <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-md bg-dusk px-3 py-1 text-xs text-white">
              Select {6 - likedRecipes.length} more recipes
            </div>
          )}
        </div>
      )}
    </div>
  );
}
